import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { ExpandMore, Star } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useContext, useMemo, useState } from 'react';
import { SelectChangeEvent, Tooltip } from '@mui/material';
import { reportMixPanel } from '@55degrees/consent-tracker';
import DataLoaderContext from '../../../contexts/DataLoaderContext';
import {
  DataTableSection,
  DataTableTitle,
  DataTableDropdown,
  DataTableLabel,
  DataTableSelect,
  DataTableFormControl,
  DataTableGrid,
  DataTableWrapper,
  FavoriteHeaderWrapper,
  FavoriteButton,
  DataTableMenuItem,
  DataTableName,
  EmptyStar,
  FullStar,
  LoadingButton,
  DataTableFooterContainer,
} from './style';
import ShowMoreFooter from './ShowMoreFooter';
import { DataTableProps, ShowDatasetOptions } from '../types';
import useSavedDatasets from '../../../hooks/useSavedDatasets';
import { MAX_NUMBER_OF_ROWS } from '../../../config';

export default function DataTable({ openDataLoaderDialog, loadDataset }: DataTableProps) {
  const {
    allStrippedDatasets,
    favoriteDatasetById,
    isLoading: isLoadingDatasets,
  } = useSavedDatasets();
  const { setTrigger } = useContext(DataLoaderContext);
  const intl = useIntl();
  const [showDatasetOption, setShowDatasetOption] = useState<ShowDatasetOptions>('all-data-sets');
  const [updatingFavoritesList, setUpdatingFavoritesList] = useState<string[]>([]);

  const handleToggleFavorite = (event: React.SyntheticEvent, datasetId: string) => {
    event.stopPropagation();
    favoriteDatasetById(datasetId);
    setUpdatingFavoritesList((prev) => prev.filter((id) => id !== datasetId));
  };

  const handleDatasetClick = (datasetId: string) => {
    loadDataset(datasetId);
    setTrigger('Landing');
  };

  const handleCellClick = (params: GridCellParams) => {
    if (params.field !== 'name') return;

    handleDatasetClick(params.row.id || params.row.uuid);
  };

  const handleClickMore = () => {
    openDataLoaderDialog('manage');
    setTrigger('Find');
    reportMixPanel('Landing Data More');
  };

  const handleChangeDropdown = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event as { target: { value: ShowDatasetOptions } };

    const mixpanelNames = {
      'all-data-sets': 'All',
      'my-favorites': 'My favorites',
    };

    reportMixPanel('Landing Data Type Select', {
      Category: mixpanelNames[value],
    });

    setShowDatasetOption(value);
  };

  const columns: GridColDef[] = [
    {
      field: 'favorite',
      headerName: 'Favorite Data Sets',
      headerClassName: 'manage-data-grid-header',
      flex: 1,
      editable: false,
      renderHeader: () => (
        <FavoriteHeaderWrapper>
          <Star color="inherit" />
        </FavoriteHeaderWrapper>
      ),
      renderCell: (params) => {
        const { dataset } = params.row;
        const { uuid, favorite } = dataset;
        const isUpdating = updatingFavoritesList.includes(uuid);

        if (isUpdating) return <LoadingButton size={14} />;
        return (
          <FavoriteButton onClick={(event) => handleToggleFavorite(event, uuid)}>
            {favorite ? <FullStar color="warning" /> : <EmptyStar />}
          </FavoriteButton>
        );
      },
    },
    {
      field: 'name',
      headerName: intl.formatMessage({
        id: 'landing.page.table.column.header.name',
        defaultMessage: 'Name',
      }),
      flex: 4,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <DataTableName key={params.id || params.row.uuid}>{params.value}</DataTableName>
        </Tooltip>
      ),
      editable: false,
    },
  ];

  const filteredRows = useMemo(() => {
    if (isLoadingDatasets) return [];
    if (!allStrippedDatasets) return [];

    const filteredDatasets = allStrippedDatasets.filter((dataset) => {
      if (showDatasetOption === 'all-data-sets') return true;
      if (showDatasetOption === 'my-favorites') return dataset.favorite;
      return false;
    });

    return filteredDatasets.map((dataset) => ({
      dataset,
      id: dataset.uuid,
      favorite: dataset.favorite,
      name: dataset.name,
    }));
  }, [isLoadingDatasets, allStrippedDatasets, showDatasetOption]);

  const showMoreRows = allStrippedDatasets && filteredRows.length > MAX_NUMBER_OF_ROWS;

  const showMoreFooterComponent = () => {
    return (
      <DataTableFooterContainer>
        <ShowMoreFooter onClick={handleClickMore} />
      </DataTableFooterContainer>
    );
  };

  return (
    <DataTableSection>
      <DataTableTitle variant="h5">
        <FormattedMessage
          id="landing.page.table.title"
          defaultMessage="Load an existing data set"
        />
      </DataTableTitle>

      <DataTableDropdown>
        <DataTableLabel variant="subtitle1" color="secondary">
          <FormattedMessage id="landing.page.table.dropdown.label" defaultMessage="Show" />
        </DataTableLabel>
        <DataTableFormControl fullWidth size="small">
          <DataTableSelect
            value={showDatasetOption}
            IconComponent={ExpandMore}
            onChange={handleChangeDropdown}
          >
            <DataTableMenuItem value="all-data-sets">
              <FormattedMessage
                id="landing.page.table.dropdown.all.data.sets"
                defaultMessage="All Data Sets"
              />
            </DataTableMenuItem>
            <DataTableMenuItem value="my-favorites">
              <FormattedMessage
                id="landing.page.table.dropdown.my.favorites"
                defaultMessage="My Favorites"
              />
            </DataTableMenuItem>
          </DataTableSelect>
        </DataTableFormControl>
      </DataTableDropdown>

      <DataTableWrapper>
        <DataTableGrid
          autoHeight
          rows={filteredRows}
          columns={columns}
          disableColumnSelector
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : '')}
          paginationModel={{ page: 0, pageSize: 10 }}
          pageSizeOptions={[10]}
          disableColumnMenu
          hideFooterSelectedRowCount
          hideFooter={isLoadingDatasets || !showMoreRows}
          rowHeight={42}
          loading={isLoadingDatasets}
          slots={{
            footer: showMoreFooterComponent,
          }}
          pagination
          onCellClick={handleCellClick}
        />
      </DataTableWrapper>
    </DataTableSection>
  );
}
